import { BRICK_GAP, BRICK_H, BRICK_HP, COLS, FIELD, GRID_PAD_X, GRID_TOP } from './constants';
import type { Brick, BrickType, LevelDef } from './types';

const TYPES = new Set<string>(['n', 's', 't', 'i', 'e', 'b', 'h', 'v']);

export function brickSize(cols = COLS): { w: number; h: number } {
  const inner = FIELD.w - GRID_PAD_X * 2;
  const w = (inner - BRICK_GAP * (cols - 1)) / cols;
  return { w, h: BRICK_H };
}

export function cellPos(col: number, row: number, cols = COLS): { x: number; y: number } {
  const { w, h } = brickSize(cols);
  return {
    x: GRID_PAD_X + col * (w + BRICK_GAP),
    y: GRID_TOP + row * (h + BRICK_GAP),
  };
}

export function neighborCells(row: number, col: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      if (dr === 0 && dc === 0) continue;
      out.push([row + dr, col + dc]);
    }
  }
  return out;
}

function hpFor(type: BrickType): number {
  const hp = BRICK_HP[type];
  return Number.isFinite(hp) ? hp : 99;
}

export function makeBrick(type: BrickType, row: number, col: number, id: number, cols = COLS): Brick {
  const { w, h } = brickSize(cols);
  const pos = cellPos(col, row, cols);
  const moving = type === 'h' || type === 'v';
  return {
    id,
    type,
    hp: hpFor(type),
    maxHp: hpFor(type),
    x: pos.x,
    y: pos.y,
    w,
    h,
    col,
    row,
    originX: pos.x,
    originY: pos.y,
    alive: true,
    moveAxis: type === 'h' ? 'x' : type === 'v' ? 'y' : 'none',
    moveRange: moving ? (type === 'h' ? 56 : 28) : 0,
    moveSpeed: moving ? 48 + (col % 3) * 12 : 0,
    movePhase: (row * 0.7 + col * 0.45) % (Math.PI * 2),
  };
}

export function parseLevel(def: LevelDef): Brick[] {
  const cols = def.rows.reduce((m, r) => Math.max(m, r.length), 0);
  const bricks: Brick[] = [];
  let id = 1;
  def.rows.forEach((row, r) => {
    for (let c = 0; c < row.length; c++) {
      const ch = row[c];
      if (!TYPES.has(ch)) continue;
      bricks.push(makeBrick(ch as BrickType, r, c, id++, cols || COLS));
    }
  });
  return bricks;
}

export function isDestructible(b: Brick): boolean {
  return b.alive && b.type !== 'i';
}

export function countDestructible(bricks: Brick[]): number {
  return bricks.filter(isDestructible).length;
}

export function isLevelClear(bricks: Brick[]): boolean {
  return countDestructible(bricks) === 0;
}

export function updateMovers(bricks: Brick[], t: number): void {
  for (const b of bricks) {
    if (!b.alive || b.moveAxis === 'none') continue;
    const wave = Math.sin(t * (b.moveSpeed / 40) + b.movePhase);
    if (b.moveAxis === 'x') {
      b.x = b.originX + wave * b.moveRange;
      b.y = b.originY;
    } else {
      b.x = b.originX;
      b.y = b.originY + wave * b.moveRange;
    }
  }
}

export function bricksInBlast(bricks: Brick[], count: number, rand = Math.random): Brick[] {
  const live = bricks.filter(isDestructible);
  const picks: Brick[] = [];
  const pool = [...live];
  while (picks.length < count && pool.length) {
    const i = Math.floor(rand() * pool.length);
    picks.push(pool.splice(i, 1)[0]);
  }
  return picks;
}
